import { createEvent, createStore } from "effector"
import { keyBy } from "lodash"

import Location from "models/Location"
import { Guid, Lmid } from "@/types"
import toastMessages from "./toastMessages"
import TenantInvoicePosition from "models/TenantInvoicePosition"
import ReservationInvoicePosition from "models/ReservationInvoicePosition"
import ReservationCartInvoicePosition from "models/ReservationCartInvoicePosition"
import UserService from "services/UserService"
import InvoiceDraftInvoicePosition from "models/InvoiceDraftInvoicePosition"
import ReservationInvoiceInvoicePosition from "models/ReservationInvoiceInvoicePosition"

export interface IArticleInfoCacheItem {
    articleId: Guid
    articleDescription: string
    articleNo: string
    articleImageUri?: string
    articleThumbnailUri?: string
}

export interface IAssetInfoCacheItem {
    assetId: Guid
    articleId: Guid
    articleDescription: string
    lmid: Lmid
    isLabeled: boolean
    linkedToAssetCollection: boolean
    isOperational: boolean
    location?: Location
    features: string[]
}

export interface IInvoicePositionCache {
    [invoicePositionNumber:string]: TenantInvoicePosition | undefined
}


// Stores
const $invoicePositionsCache = createStore<IInvoicePositionCache>({})


// Events
const updateInvoicePositionsCache = createEvent<(TenantInvoicePosition)[]>()
const reset = createEvent()


// Listeners
$invoicePositionsCache
    .on(updateInvoicePositionsCache, (state, payload) => ({
            ...state,
            ...keyBy(payload, 'number')
        }))
    .on(reset, () => ({}))

// Utils

// fill articles and assets info into cart
export async function hydrateInvoicePositions<IP extends ReservationInvoicePosition | ReservationInvoiceInvoicePosition | ReservationCartInvoicePosition | InvoiceDraftInvoicePosition>(reservationInvoicePositions: IP[], options: { withCache?: boolean } = {}): Promise<IP[]> {
    try {
        let invoicePositions: TenantInvoicePosition[]
        if (options.withCache) {
            const invoicePositionsCache = $invoicePositionsCache.getState()
            const hasNotCachedInvoicePositions = reservationInvoicePositions.some(rip => !invoicePositionsCache[rip.number])
            if (hasNotCachedInvoicePositions) {
                invoicePositions = (await UserService.getTenantInvoicePositions()).entities
                updateInvoicePositionsCache(invoicePositions)
            } else {
                invoicePositions = Object.values(invoicePositionsCache) as TenantInvoicePosition[]
            }
        } else {
            const hasNotHydratedInvoicePositions = reservationInvoicePositions.some(rip => !rip.invoicePosition)
            if (!hasNotHydratedInvoicePositions) {
                return reservationInvoicePositions
            }
            invoicePositions = (await UserService.getTenantInvoicePositions()).entities
        }
        return reservationInvoicePositions.map(rip => {
            rip.invoicePosition = invoicePositions.find(ip => ip.number === rip.number)
            return rip
        })
    } catch(e) {
        console.log(e)
        toastMessages.showSystemError()
    }
    return reservationInvoicePositions
}

export default {
    reset,
}
